import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUSES = ["processing", "shipped", "delivered", "cancelled", "returned", "refunded"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [sf, setSf] = useState("");
  const [sel, setSel] = useState(null);

  const load = () => api.get("/admin/orders", { params: { status: filter || undefined, q: q || undefined, storefront: sf || undefined } }).then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, [filter, sf]);

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Orders</h1>
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {STATUSES.map((s) => (
          <button key={s} data-testid={`order-filter-${s}`} onClick={() => setFilter(filter === s ? "" : s)}
            className={`border rounded-lg p-3 text-center ${filter === s ? "border-neutral-900 bg-neutral-900 text-white" : "border-neutral-200 bg-white"}`}>
            <div className="text-xl font-serif">{orders.filter((o) => o.status === s).length}</div>
            <div className="text-[0.65rem] uppercase tracking-wide mt-1">{s}</div>
          </button>
        ))}
      </div>
      <div className="flex gap-3 mb-5">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 flex gap-3">
          <input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order # or email" className="flex-1 h-10 px-3 border border-neutral-300 rounded text-sm" />
          <button className="px-5 h-10 bg-neutral-900 text-white rounded text-sm">Search</button>
        </form>
        <select value={sf} onChange={(e) => setSf(e.target.value)} className="h-10 px-3 border border-neutral-300 rounded text-sm bg-white">
          <option value="">All Storefronts</option><option value="jewelry">Jewelry</option><option value="men">Men</option><option value="women">Women</option>
        </select>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-lg p-16 text-center text-neutral-500">No orders yet.</div>
      ) : (
        <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-neutral-500 text-xs uppercase"><tr><th className="text-left p-4">Order</th><th className="text-left p-4">Customer</th><th className="text-left p-4">Storefronts</th><th className="text-left p-4">Total</th><th className="text-left p-4">Payment</th><th className="text-left p-4">Status</th></tr></thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.id} data-testid={`order-row-${o.order_number}`} onClick={() => setSel(o)} className="border-t border-neutral-100 cursor-pointer hover:bg-neutral-50">
                  <td className="p-4 font-medium">{o.order_number}</td>
                  <td className="p-4 text-neutral-600">{o.email}</td>
                  <td className="p-4 text-neutral-500">{(o.storefronts || []).join(", ")}</td>
                  <td className="p-4">${o.total_usd} {o.currency}</td>
                  <td className="p-4"><span className={o.payment_status === "paid" ? "text-green-600" : "text-amber-600"}>{o.payment_status}</span></td>
                  <td className="p-4"><span className="text-xs bg-neutral-100 px-2 py-1 rounded uppercase">{o.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {sel && <OrderDrawer order={sel} onClose={() => setSel(null)} onUpdate={() => { load(); }} setSel={setSel} />}
    </div>
  );
}

function OrderDrawer({ order, onClose, onUpdate, setSel }) {
  const [o, setO] = useState(order);
  const [note, setNote] = useState("");
  const [tracking, setTracking] = useState(order.tracking_number || "");

  const update = async (patch) => {
    const { data } = await api.put(`/admin/orders/${o.id}`, patch);
    setO(data); setSel(data); onUpdate(); toast.success("Order updated.");
  };
  const addNote = async () => {
    if (!note) return;
    const { data } = await api.post(`/admin/orders/${o.id}/note`, { note });
    setO(data); setNote(""); toast.success("Note added.");
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[200] flex justify-end" onClick={onClose}>
      <div className="bg-white w-full max-w-xl h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="order-drawer">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-serif text-2xl">{o.order_number}</h2><button onClick={onClose}><X size={22} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3 mb-6">
          <Field label="Status"><select data-testid="order-status-select" value={o.status} onChange={(e) => update({ status: e.target.value })} className="w-full h-10 px-2 border border-neutral-300 rounded text-sm">{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Payment"><select value={o.payment_status} onChange={(e) => update({ payment_status: e.target.value })} className="w-full h-10 px-2 border border-neutral-300 rounded text-sm">{["pending", "paid", "refunded", "failed"].map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Shipping"><select value={o.shipping_status} onChange={(e) => update({ shipping_status: e.target.value })} className="w-full h-10 px-2 border border-neutral-300 rounded text-sm">{["unfulfilled", "fulfilled", "in_transit", "delivered"].map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Tracking #"><div className="flex gap-1"><input value={tracking} onChange={(e) => setTracking(e.target.value)} className="flex-1 h-10 px-2 border border-neutral-300 rounded text-sm" /><button onClick={() => update({ tracking_number: tracking })} className="px-3 bg-neutral-900 text-white rounded text-xs">Save</button></div></Field>
        </div>

        <h3 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Items</h3>
        <div className="border border-neutral-200 rounded mb-6">
          {o.items.map((it, i) => (
            <div key={i} className="flex gap-3 p-3 border-b border-neutral-100 last:border-0">
              <img src={it.image} alt="" className="w-12 h-16 object-cover rounded" />
              <div className="flex-1 text-sm"><div className="font-medium">{it.name}</div><div className="text-xs text-neutral-500">{it.storefront} · {it.sku} · Qty {it.qty}</div></div>
              <div className="text-sm">${it.line_total_usd}</div>
            </div>
          ))}
        </div>

        <div className="text-sm space-y-1 border border-neutral-200 rounded p-4 mb-6">
          <Line l="Subtotal" v={`$${o.subtotal_usd}`} />
          {o.discount_usd > 0 && <Line l={`Discount (${o.discount_code})`} v={`– $${o.discount_usd}`} />}
          <Line l={o.shipping_name} v={`$${o.shipping_usd}`} />
          <Line l={o.tax_label} v={`$${o.tax_usd}`} />
          {o.duty_usd > 0 && <Line l="Duties (est.)" v={`$${o.duty_usd}`} />}
          <div className="flex justify-between font-serif text-lg pt-2 border-t border-neutral-200"><span>Total</span><span>${o.total_usd} {o.currency}</span></div>
          <div className="text-xs text-neutral-400 pt-1">Snapshot at purchase · rate {o.exchange_rate}</div>
        </div>

        <h3 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Shipping Address</h3>
        <div className="text-sm text-neutral-600 mb-6">{o.shipping_address?.name}<br />{o.shipping_address?.address}, {o.shipping_address?.city}<br />{o.shipping_address?.province} {o.shipping_address?.postal}, {o.shipping_address?.country}</div>

        <h3 className="text-xs uppercase tracking-wide text-neutral-500 mb-2">Internal Notes</h3>
        {(o.notes || []).map((n, i) => <div key={i} className="text-sm bg-neutral-50 p-2 rounded mb-1">{n.text} <span className="text-xs text-neutral-400">— {n.by}</span></div>)}
        <div className="flex gap-2 mt-2">
          <input data-testid="order-note-input" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add note…" className="flex-1 h-10 px-3 border border-neutral-300 rounded text-sm" />
          <button onClick={addNote} className="px-4 bg-neutral-900 text-white rounded text-sm">Add</button>
        </div>
      </div>
    </div>
  );
}
const Field = ({ label, children }) => <div><div className="text-xs uppercase tracking-wide text-neutral-500 mb-1">{label}</div>{children}</div>;
const Line = ({ l, v }) => <div className="flex justify-between text-neutral-600"><span>{l}</span><span>{v}</span></div>;
